import { supabase } from './supabaseClient';
import { ModuleActivity, spaetestes } from './moduleActivity';

/**
 * Laedt je Kachel des Projekt-Hubs den juengsten Zeitstempel. Jede Abfrage
 * ist `limit 1` und laeuft parallel; jede scheitert fuer sich fail-open
 * (fehlende Tabelle in einer Umgebung, fehlende Spalte, RLS) und liefert
 * dann null — die Kachel zeigt „noch nichts", der Hub bleibt bedienbar.
 */
type Abfrage = any;

const juengster = async (
    tabelle: string,
    spalte: string,
    projectId: string,
    filter?: (q: Abfrage) => Abfrage,
): Promise<string | null> => {
    try {
        let q: Abfrage = supabase.from(tabelle).select(spalte).eq('project_id', projectId);
        if (filter) q = filter(q);
        const { data, error } = await q.order(spalte, { ascending: false, nullsFirst: false }).limit(1);
        if (error) return null;
        const wert = data?.[0]?.[spalte];
        return typeof wert === 'string' && wert ? wert : null;
    } catch {
        return null;
    }
};

/** Mehrere Tabellen desselben Moduls: die spaeteste Aenderung zaehlt. */
const juengsterVon = async (tabellen: string[], spalte: string, projectId: string): Promise<string | null> =>
    spaetestes(...await Promise.all(tabellen.map(t => juengster(t, spalte, projectId))));

/**
 * Modul mit Kopfsatz und Kindtabellen (A+S-Plan, Baustellenordnung). Eine
 * Aenderung an einem Kind bewegt den Kopf NICHT — deshalb muessen ALLE Kinder
 * gefragt werden. Damit die vielen Abfragen nicht bei jedem Projekt anfallen,
 * zuerst der Kopf: ohne Kopfsatz gibt es keine Kinder, und die Kachel bleibt
 * nach einer einzigen Abfrage bei „noch nichts".
 */
const juengsterMitKindern = async (kopf: string, kinder: string[], projectId: string): Promise<string | null> => {
    const kopfWert = await juengster(kopf, 'updated_at', projectId);
    if (!kopfWert) return null;
    return spaetestes(kopfWert, await juengsterVon(kinder, 'updated_at', projectId));
};

/** Alle Kindtabellen mit `project_id` und `updated_at` laut asplan.sql. */
const ASPLAN_KINDER = [
    'asplan_rasterfelder', 'asplan_proben', 'asplan_cluster', 'asplan_taetigkeiten',
    'asplan_schutzklassen', 'asplan_rollen', 'asplan_stufungen', 'asplan_beteiligte',
    'asplan_unternehmen', 'asplan_unterlagen', 'asplan_bausteine', 'asplan_skizzen', 'asplan_pruefliste',
];

/** Alle Kindtabellen mit `project_id` laut bauordnung.sql (`bauordnung_kapitel` ist Firmenbestand ohne Projekt). */
const BAUORDNUNG_KINDER = ['bauordnung_abschnitte', 'bauordnung_regeln', 'bauordnung_rollen', 'bauordnung_luecken'];

export const fetchModuleActivity = async (projectId: string): Promise<ModuleActivity> => {
    if (!projectId || projectId === 'temp-id') return {};

    const [
        dokumente, archiv, begehung, sendeverlauf, planung,
        sigeplanUpd, sigeplanNeu, asplan, einweisung, bauordnung,
    ] = await Promise.all([
        juengster('project_documents', 'updated_at', projectId),
        // Archiv = wann zuletzt archiviert; Begehung = Tag der letzten
        // Begehung. Zwei Fragen, zwei Spalten — ein rueckdatierter Bericht
        // trennt sie. Vorankuendigungs- und Unterlagen-Archiv liegen im
        // Projekt-JSON und kommen in aktivitaetAusProjektJson dazu.
        juengster('report_history', 'created_at', projectId),
        juengster('report_history', 'date', projectId),
        // Nur was wirklich rausging: `email_logs` fuehrt auch gescheiterte
        // Versuche (status 'error'), und „Zuletzt gesendet" darf keinen
        // Fehlversuch als Versand ausgeben.
        juengster('email_logs', 'sent_at', projectId, q => q.eq('status', 'success')),
        juengsterVon(['planungsphasen', 'planungsfragen', 'planungshinweise'], 'updated_at', projectId),
        // `sige_projekte` hat kein SQL im Repo; ob `updated_at` existiert,
        // ist je Umgebung offen. Beide Spalten fragen — die Abfrage ohne
        // die Spalte faellt still auf null, die andere traegt.
        juengster('sige_projekte', 'updated_at', projectId, q => q.is('deleted_at', null)),
        juengster('sige_projekte', 'created_at', projectId, q => q.is('deleted_at', null)),
        juengsterMitKindern('asplaene', ASPLAN_KINDER, projectId),
        juengster('sige_einweisungen', 'updated_at', projectId),
        juengsterMitKindern('bauordnungen', BAUORDNUNG_KINDER, projectId),
    ]);

    return {
        dokumente,
        archiv,
        begehung,
        sendeverlauf,
        planung,
        sigeplan: spaetestes(sigeplanUpd, sigeplanNeu),
        asplan,
        einweisung,
        bauordnung,
    };
};
